import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LOGO_URL } from "@/components/brand/Logo";
import {
  Download, Copy, ExternalLink, ClipboardList, Handshake, Star,
  Sparkles, Instagram, MessageSquare, Video,
} from "lucide-react";

/**
 * Marketing / Materials page.
 * Produces clean, high-resolution QR codes (PNG or SVG) that Joylance can
 * drop into her own designed business cards, flyers, and print materials.
 */

const SITE_BASE = (() => {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  try {
    const u = new URL(backend);
    return `${u.protocol}//${u.hostname}`;
  } catch {
    return backend;
  }
})();

const MATERIALS = [
  {
    key: "book",
    label: "Book a Consultation",
    subtitle: "Landing page · About + services + booking form",
    caption: "Scan to explore the app",
    icon: Handshake,
    path: "/book",
    color: "#5D3A1F",
  },
  {
    key: "discover",
    label: "Needs Assessment",
    subtitle: "10-question deep dive · Prospects fill it themselves",
    caption: "Scan to complete your Needs Assessment",
    icon: ClipboardList,
    path: "/discover",
    color: "#C28A2E",
  },
  {
    key: "testimonials",
    label: "Testimonial Wall",
    subtitle: "What our clients are saying",
    caption: "Scan to read client testimonials",
    icon: Star,
    path: "/testimonials",
    color: "#8B5A2B",
  },
];

const SOCIALS = [
  { icon: Instagram, label: "Instagram", handle: "@theccsapproach", url: "https://instagram.com/theccsapproach" },
  { icon: MessageSquare, label: "Threads", handle: "@theccsapproach", url: "https://threads.net/@theccsapproach" },
  { icon: Video, label: "TikTok", handle: "@theccsapproach", url: "https://tiktok.com/@theccsapproach" },
];

/**
 * Download the QR code as a high-resolution transparent-background PNG,
 * ready to drop into any design layout.
 */
const downloadQrPng = (svgEl, filename, transparent = false) => {
  if (!svgEl) return;
  const svgData = new XMLSerializer().serializeToString(svgEl);
  const svg64 = btoa(unescape(encodeURIComponent(svgData)));
  const img = new Image();
  img.onload = () => {
    const scale = 8; // 8× for print quality
    const size = 240 * scale;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!transparent) {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, size, size);
    }
    ctx.drawImage(img, 0, 0, size, size);
    const a = document.createElement("a");
    a.download = filename;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };
  img.src = "data:image/svg+xml;base64," + svg64;
};

/** Download the QR as an SVG (vector — infinitely scalable, best for print). */
const downloadQrSvg = (svgEl, filename) => {
  if (!svgEl) return;
  const svgData = new XMLSerializer().serializeToString(svgEl);
  const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.download = filename;
  a.href = url;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

const MarketingPage = () => {
  const [selected, setSelected] = useState(MATERIALS[0].key);
  const qrRefs = useRef({});

  const cards = MATERIALS.map((m) => ({ ...m, url: `${SITE_BASE}${m.path}` }));
  const active = cards.find((c) => c.key === selected);

  const copyText = async (text, msg = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch {
      toast.error("Copy failed — long-press the text above to copy manually");
    }
  };

  return (
    <div className="space-y-8" data-testid="marketing-page">
      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Marketing · Materials</div>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] mt-2">
          Your QR codes, ready for your designs
        </h1>
        <p className="text-sm text-[#6B5944] mt-2 max-w-2xl leading-relaxed">
          Download clean, high-resolution QR codes to drop into your own business cards,
          flyers, brochures, and print materials. Both PNG (raster) and SVG (vector) formats available.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-[#EBE3D2] pb-1" data-testid="marketing-tabs">
        {cards.map((c) => {
          const isActive = selected === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setSelected(c.key)}
              data-testid={`marketing-tab-${c.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-medium transition ${
                isActive
                  ? "bg-white border border-[#EBE3D2] border-b-white text-[#2A1810] shadow-sm"
                  : "text-[#6B5944] hover:text-[#5D3A1F]"
              }`}
            >
              <c.icon className="w-4 h-4" style={{ color: c.color }} />
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Selected material */}
      {active && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* QR preview */}
          <div className="lg:col-span-2 flex flex-col items-center">
            <div
              className="p-6 bg-white rounded-2xl border-2 shadow-[0_20px_60px_rgba(45,24,16,0.10)]"
              style={{ borderColor: `${active.color}30` }}
              data-testid={`qr-preview-${active.key}`}
            >
              <QRCodeSVG
                ref={(el) => { qrRefs.current[active.key] = el; }}
                value={active.url}
                size={280}
                level="H"
                fgColor="#2A1810"
                bgColor="#FFFFFF"
                imageSettings={{
                  src: LOGO_URL,
                  height: 56,
                  width: 56,
                  excavate: true,
                }}
              />
            </div>
            <div className="mt-4 text-xs text-[#6B5944] italic text-center max-w-xs">
              Suggested caption: <span className="font-semibold text-[#5D3A1F]">"{active.caption}"</span>
            </div>
          </div>

          {/* Actions & info */}
          <div className="lg:col-span-3 space-y-4">
            <div className="ccs-card p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">{active.subtitle}</div>
              <h3 className="font-display text-xl font-semibold text-[#2A1810] mt-2">{active.label}</h3>
              <div className="mt-3 text-xs text-[#6B5944] break-all font-mono p-3 bg-[#FDFBF5] border border-[#EBE3D2] rounded-lg" data-testid={`marketing-url-${active.key}`}>
                {active.url}
              </div>

              <div className="mt-5 space-y-2">
                <Button
                  onClick={() => downloadQrPng(qrRefs.current[active.key], `CCS-QR-${active.key}.png`)}
                  data-testid={`marketing-download-png-${active.key}`}
                  className="w-full bg-[#5D3A1F] hover:bg-[#4A2E18] text-white rounded-xl h-11"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PNG (high-resolution · white background)
                </Button>
                <Button
                  onClick={() => downloadQrSvg(qrRefs.current[active.key], `CCS-QR-${active.key}.svg`)}
                  data-testid={`marketing-download-svg-${active.key}`}
                  variant="outline"
                  className="w-full border-[#C28A2E]/40 text-[#5D3A1F] rounded-xl h-11 hover:bg-[#C28A2E]/10"
                >
                  <Download className="w-4 h-4 mr-2" /> Download SVG (vector · perfect for print)
                </Button>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Button
                    variant="outline"
                    onClick={() => copyText(active.url, "URL copied")}
                    data-testid={`marketing-copy-${active.key}`}
                    className="rounded-xl"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy URL
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(active.url, "_blank")}
                    data-testid={`marketing-open-${active.key}`}
                    className="rounded-xl"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Preview
                  </Button>
                </div>
              </div>
            </div>

            <div className="ccs-card ccs-grain p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Design tips
              </div>
              <ul className="mt-3 space-y-2 text-sm text-[#5D3A1F] leading-relaxed">
                <li>🎨 <strong>PNG</strong> — best for social media, digital slides, quick print. Scales cleanly up to about 4×4 inches.</li>
                <li>📐 <strong>SVG</strong> — best for professional print (business cards, flyers, banners). Infinitely scalable, never loses quality.</li>
                <li>📇 <strong>Minimum size</strong> — keep the QR at least 0.8 inch (2 cm) square so all scanners can read it.</li>
                <li>💡 <strong>Contrast</strong> — always place on a light background with dark ink for reliable scanning.</li>
                <li>✍️ <strong>Caption</strong> — pair with a short call to action so people know what happens when they scan.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Brand kit */}
      <div className="ccs-card p-6" data-testid="brand-kit">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Brand kit — for your designer</div>
        <h3 className="font-display text-xl font-semibold text-[#2A1810] mt-2">Everything you need for your materials</h3>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo download */}
          <div>
            <div className="text-xs font-semibold text-[#5D3A1F] mb-2">Logo</div>
            <div className="p-4 bg-white border border-[#EBE3D2] rounded-xl flex items-center gap-3">
              <img src={LOGO_URL} alt="The CCS Approach logo" className="w-14 h-14 object-contain" />
              <a
                href={LOGO_URL}
                download="CCS-Approach-Logo.png"
                className="text-xs text-[#5D3A1F] font-semibold hover:underline inline-flex items-center gap-1.5"
                data-testid="brand-logo-download"
              >
                <Download className="w-3.5 h-3.5" /> Download logo
              </a>
            </div>
          </div>

          {/* Color palette */}
          <div>
            <div className="text-xs font-semibold text-[#5D3A1F] mb-2">Colours</div>
            <div className="grid grid-cols-4 gap-2" data-testid="brand-colors">
              {[
                { name: "Brown", hex: "#5D3A1F" },
                { name: "Gold", hex: "#C28A2E" },
                { name: "Rust", hex: "#8B5A2B" },
                { name: "Cream", hex: "#FDFBF5" },
              ].map((c) => (
                <button
                  key={c.hex}
                  onClick={() => copyText(c.hex, `${c.hex} copied`)}
                  className="text-left p-2 border border-[#EBE3D2] rounded-lg hover:shadow-md transition"
                  data-testid={`brand-color-${c.name.toLowerCase()}`}
                >
                  <div className="w-full h-10 rounded" style={{ backgroundColor: c.hex, border: c.hex === "#FDFBF5" ? "1px solid #EBE3D2" : "none" }} />
                  <div className="text-[10px] font-semibold text-[#2A1810] mt-1.5">{c.name}</div>
                  <div className="text-[9px] text-[#6B5944] font-mono">{c.hex}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Copy assets */}
          <div className="md:col-span-2">
            <div className="text-xs font-semibold text-[#5D3A1F] mb-2">Copy-ready text for your card</div>
            <div className="space-y-2">
              {[
                { label: "Title", value: "Leadership, Communication & Service Development Facilitator" },
                { label: "Tagline", value: "Communicate · Collaborate · Serve" },
                { label: "Scripture", value: '"Let everything you do be done in love." — 1 Corinthians 16:14' },
                { label: "Website URL", value: `${SITE_BASE}/book` },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#C28A2E] font-bold w-20 shrink-0">{item.label}</div>
                  <div className="flex-1 text-xs text-[#2A1810] p-2 bg-[#FDFBF5] border border-[#EBE3D2] rounded font-mono">{item.value}</div>
                  <button
                    onClick={() => copyText(item.value, `${item.label} copied`)}
                    className="p-1.5 hover:bg-[#FDFBF5] rounded"
                    data-testid={`brand-copy-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <Copy className="w-3.5 h-3.5 text-[#5D3A1F]" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Social handles */}
          <div className="md:col-span-2">
            <div className="text-xs font-semibold text-[#5D3A1F] mb-2">Social media handles</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="brand-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 border border-[#EBE3D2] rounded-lg hover:border-[#C28A2E]/60 hover:bg-[#FDFBF5] transition text-sm"
                  data-testid={`brand-social-${s.label.toLowerCase()}`}
                >
                  <s.icon className="w-4 h-4 text-[#C28A2E]" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B5944] font-bold">{s.label}</div>
                    <div className="text-xs font-mono text-[#2A1810]">{s.handle}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingPage;
